import S, { DataSignal } from "s-js";
import { range } from "src-lib/util/arrays";
import { delegateEvent } from "src-lib/util/dom";
import * as Surplus from "surplus";
import {
    createFormState,
    Form,
    InputCheckbox,
    InputCheckboxIndeterminate,
    InputField,
    inputState,
    InputState,
    InputStateArray,
    InputText,
    resetInputState,
    Select,
} from "surplus-forms";
import { Button, ButtonImg, ButtonPri, IconMinus, IconPlus } from "../util/decorators";
import { Collapse } from "./animations";
import { SwitchCheckbox } from "./input";
import { JSXChildren } from "./layout";
import { Loader } from "./loader/Loader";
import { ErrorNotification, IconInfo, InfoNotification, InlineError } from "./notification";
import { Tooltip } from "./Tooltip";

const FILTER_INPUT_TIMEOUT = 210;

export function DynamicInputForm() {
    return <FormContent />;
}

function randomComplexInput(
    options: DataSignal<{ value: string; id: string }[]>,
): InputComplexState {
    const a = getPredictableRandom() % 2 === 0;
    const maxOptions = a ? 2 : 3; // if a is selected don't pick last drop down option. Used as a quick validation
    const index = getPredictableRandom() % maxOptions;
    return {
        a,
        b: S.sample(options)[index].value,
        c: "",
        type: inputTypes[getPredictableRandom() % (inputTypes.length - 1)],
    };
}

export function Divider() {
    return <hr style={{ marginLeft: "-1.6rem", marginRight: "-1.6rem" }} />;
}

export function CardContainer({ children }: JSXChildren) {
    return <div class="pl2 pr2">{children}</div>;
}

export function Card({
    children,
    title,
}: {
    children: JSX.Element | (JSX.Element | undefined | false)[];
    title?: JSX.Element | string;
}) {
    return (
        <div class="card">
            {title && <div class="pt1 pb1">{title}</div>}
            <div class="pb1">{children}</div>
        </div>
    );
}

function FormContent() {
    const filterInput = S.data(inputState("filter", ""));
    const options = S.data([
        { value: "1", id: "1" },
        { value: "2", id: "2" },
        { value: "5", id: "3" },
        { value: "6", id: "4" },
    ]);
    const initialForm = {
        checkboxArray: [false, false, true],
        showDebug: false,
        complexInputArray: range(0, 10).map((it) => randomComplexInput(options)),
    };
    const selectAll = S.data(false);
    const dummy = S.data(1);

    const form = createFormState(initialForm, [
        (form) => {
            return form.checkboxArray().value()[2]().value &&
                form.complexInputArray().value().length >= 260
                ? 300
                : 0;
        },
    ]);
    setInterval(() => dummy(dummy() + 1), 500);

    return (
        <div class="container">
            <Card>
                <div class="pt2" />
                <CardContainer>
                    <Form value={form} onReset={() => filterInput(inputState("filter", ""))}>
                        <div>
                            <SwitchCheckbox data={form.showDebug} label="Show InputState?" />
                            <Divider />
                            <div
                                class="mb2"
                                style={{
                                    marginTop: "-1.6rem",
                                    marginLeft: "-1.6rem",
                                    marginRight: "-1.6rem",
                                }}>
                                <InfoNotification isOpen={S(() => true)}>
                                    {S(() => (
                                        <span>
                                            <strong>Validation</strong>: 3rd checkbox cannot be
                                            selected if more than 259 complex inputs{" "}
                                        </span>
                                    ))}
                                </InfoNotification>
                                <InfoNotification isOpen={S(() => true)}>
                                    {S(() => (
                                        <span>
                                            <strong>Validation</strong>: All checkboxes must be
                                            selected
                                        </span>
                                    ))}
                                </InfoNotification>
                            </div>
                            <div class="row">
                                <div class="column">
                                    <label
                                        class="ml0 mr1"
                                        for="select-all"
                                        onMouseDown={preventEventDefault}>
                                        <InputCheckboxIndeterminate
                                            selectAllName="select-all"
                                            checkboxSignals={form.checkboxArray}
                                            selectAll={selectAll}
                                        />
                                        <span>Select All</span>
                                    </label>
                                    <div>
                                        {S.sample(form.checkboxArray).value.map((sig, _, i) => (
                                            <label
                                                class="label-inline ml0 mr1"
                                                for={sig().name}
                                                onMouseDown={preventEventDefault}>
                                                <InputCheckbox data={sig} required />
                                                <span>Check box[{i}]</span>
                                            </label>
                                        ))}
                                    </div>
                                    <div class="mt1">
                                        <ErrorNotification
                                            isOpen={S(() => form.status().globalError === 300)}>
                                            {S(
                                                () =>
                                                    "Cannot select third checkbox when more than 259 complex inputs are created.",
                                            )}
                                        </ErrorNotification>
                                        <InlineError id="select-all" sig={form.checkboxArray}>
                                            {S(() => "All checkboxes must be selected")}
                                        </InlineError>
                                    </div>

                                    <Collapse isOpen={S(() => form.showDebug().value)}>
                                        <StateViewer>
                                            {JSON.stringify(
                                                desigFormState(form.checkboxArray()),
                                                null,
                                                2,
                                            )}
                                        </StateViewer>
                                    </Collapse>
                                </div>
                            </div>
                            <Divider />
                            <div
                                class="mb2"
                                style={{
                                    marginTop: "-1.6rem",
                                    marginLeft: "-1.6rem",
                                    marginRight: "-1.6rem",
                                }}>
                                <InfoNotification isOpen={S(() => true)}>
                                    {S(() => (
                                        <span>
                                            <strong>Validation</strong>: The text input can't be the
                                            same as the color
                                        </span>
                                    ))}
                                </InfoNotification>
                                <InfoNotification isOpen={S(() => true)}>
                                    {S(() => (
                                        <span>
                                            <strong>Validation</strong>: If the checkbox is enabled,
                                            then the &lt;Select&gt; can't be 6
                                        </span>
                                    ))}
                                </InfoNotification>
                            </div>
                            <Button class="mb1" onClick={(e) => addComplexFormInputs()}>
                                Add 25 Complex form inputs
                            </Button>
                            <FilterList
                                options={options}
                                data={form.complexInputArray}
                                onCopy={onCopyRow}
                                onDelete={onDeleteRow}
                                filterInput={filterInput}
                            />
                            <div>
                                <Collapse isOpen={S(() => form.showDebug().value)}>
                                    <StateViewer>
                                        {JSON.stringify(
                                            desigFormState(form.complexInputArray()),
                                            null,
                                            2,
                                        )}
                                    </StateViewer>
                                </Collapse>
                            </div>

                            <ButtonPri
                                class="mr1"
                                type="submit"
                                disabled={form.status().isSubmitted}>
                                Save
                            </ButtonPri>
                            <Button type="reset">Reset</Button>
                            <div class="mt1">
                                <ErrorNotification
                                    isOpen={S(
                                        () =>
                                            form.status().submissions > 0 &&
                                            form.status().globalError !== 0,
                                    )}>
                                    {S(() => "Cannot submit until form errors are corrected.")}
                                </ErrorNotification>
                            </div>
                        </div>
                    </Form>
                    <Divider />
                    <p>
                        <div class="mb1">
                            <em>Form Status</em>
                        </div>
                        <StateViewer>
                            {JSON.stringify(desigFormState(form.status()), null, 2)}
                        </StateViewer>
                    </p>
                </CardContainer>
            </Card>
            <div class="mb2" />
        </div>
    );

    function addComplexFormInputs() {
        S.freeze(() => {
            for (let i = 0; i < 25; i++) {
                S.sample(form.complexInputArray).value.push(
                    S.data(inputState("complex" + i, randomComplexInput(options))),
                );
            }
        });
    }

    function onDeleteRow(data: DataSignal<InputComplexState>) {
        const sampled = S.sample(data);
        const index = S.sample(S.sample(form.complexInputArray).value).findIndex(
            (it) => S.sample(it).value === sampled,
        );
        if (index === -1) return;

        S.sample(form.complexInputArray).value.splice(index, 1);
    }

    function onCopyRow(data: DataSignal<InputComplexState>) {
        const sampled = S.sample(data);
        const index = S.sample(S.sample(form.complexInputArray).value).findIndex(
            (it) => S.sample(it).value === sampled,
        );
        if (index === -1) return;

        const original = S.sample(S.sample(form.complexInputArray).value)[index];

        const copy: InputState<InputComplexState> = { ...S.sample(original) };
        copy.startValue = { ...copy.value };
        copy.value = { ...copy.value };
        resetInputState(copy);
        S.sample(form.complexInputArray).value.splice(index + 1, 0, S.data(copy));
    }
}

// function toggleFormDisable(form: FormState<unknown>) {
//     const isDisabled: boolean = S.sample(form.inputs).every(sig => {
//         return S.sample(sig).isDisabled;
//     });
//
//     S.freeze(() => {
//         form.inputs.forEach(sig => {
//             const curr = S.sample(sig);
//             console.log("curr", curr);
//             if ("isArrayInput" in curr) {
//                 S.sample(curr.value).forEach(sigArray => {
//                     const currArray = S.sample(sigArray);
//                     sigArray({ ...currArray, __v: currArray.__v + 1, isDisabled: !isDisabled });
//                 });
//             }
//             sig({ ...curr, __v: curr.__v + 1, isDisabled: !isDisabled });
//         });
//     });
// }

export function desigFormState(o: DataSignal<any> | any): any {
    if (typeof o === "function") return desigFormState(o());
    if (typeof o !== "object") return o; // return strings, numbers, and primitives

    // Iterate over an object and convert and execute DataSignals to get their values
    let res = {} as any;
    for (const key of Object.keys(o)) {
        if (key === "forEachInput") continue;
        res[key] = desigFormState(o[key]);
    }
    return res;
}

function preventEventDefault(e: MouseEvent) {
    e.preventDefault();
}

function StateViewer(props: { children: any }) {
    return (
        <pre>
            <code style={{ overflowX: "auto" }}>{props.children}</code>
        </pre>
    );
}

const inputTypes = ["blue", "Green", "red", "orange", "purple"] as const;
type InputType = typeof inputTypes[number];

type InputComplexState = {
    a: boolean;
    b: string;
    c: string;
    type: InputType;
};

let __uuids = 0;

function twoWayInputState<T, U>(
    name: string,
    value: DataSignal<T>,
    pick: (value: T) => U,
    output: (value: U) => Partial<T>,
): DataSignal<InputState<U>> {
    const signal: DataSignal<InputState<U>> = S.data(inputState(name, pick(S.sample(value))));
    S.on(
        value,
        () => {
            // Just pass it through to signal. The S computation will resolve updates
            const incoming = pick(value());
            if (incoming !== S.sample(signal).value) {
                signal({ ...S.sample(signal), value: incoming });
            }
        },
        undefined,
        true,
    );

    return signal;
}

export function FilterList(props: {
    data: DataSignal<InputStateArray<InputComplexState>>;
    options: DataSignal<{ value: string; id: string }[]>;
    onDelete: (index: DataSignal<InputComplexState>) => void;
    onCopy: (index: DataSignal<InputComplexState>) => void;
    filterInput: DataSignal<InputState<string>>;
}) {
    let refFilterComponentsContainer: HTMLDivElement | undefined;
    const filter = S.data("");
    const filterListByText = S.sample(props.data)
        .value.map((it) => it)
        .filter((it) => {
            const trimmed = filter().toLowerCase();
            const value = S.sample(it).value;
            return (
                value.b.toLowerCase().indexOf(trimmed) !== -1 ||
                value.c.toLowerCase().indexOf(trimmed) !== -1 ||
                value.type.toLowerCase().indexOf(trimmed) !== -1
            );
        });

    const isTyping = S.value(false);
    let filterUpdateValue = S.sample(props.filterInput).value,
        lastInputTime = -1,
        timer = -1;

    S.on(
        props.filterInput,
        () => {
            if (props.filterInput().value === S.sample(filter)) return;

            isTyping(true);
            debounceInput(props.filterInput().value);
        },
        undefined,
        true,
    );

    S.on(
        filter,
        () => {
            isTyping(false);
        },
        undefined,
        true,
    );

    window.requestAnimationFrame(() => {
        if (!refFilterComponentsContainer) return;
        const ref = refFilterComponentsContainer.parentElement;
        if (!ref) return;

        delegateEvent(refFilterComponentsContainer, ["DIV", "moveRow"], {
            dragenter: (e, uid) => {},
        });
        // delegateEvent(refFilterComponentsContainer, ["DIV", "complexInput"],
        //     {
        //         drop: (e, uid) => {
        //             console.log("drop", e);
        //         },
        //
        //     })
        //     delegateEvent(refFilterComponentsContainer, ["DIV", "flexVEnd"],
        //         {
        //             mouseenter: (e, uid) => {
        //                 e.style.transform = "translateX(1rem)";
        //             },
        //             mouseleave: e => {
        //                 e.style.transform = "";
        //
        //             },
        //         }, undefined);
        //
    });

    return (
        <div class="mb2">
            <span class="disp-none">Filter:</span>
            <InputText
                trimWhitespace
                style={{ margin: "0", paddingLeft: "7.2rem", paddingRight: "7.2rem" }}
                data={props.filterInput}
                debounce={0}
                prefixComponent={
                    <label class="fw-600 colour-c64-grey5" for={S.sample(props.filterInput).name}>
                        Filter:
                    </label>
                }
                postfixComponent={
                    <div class="pr1 colour-c64-grey5 fw-600">
                        {filterListByText().length} / {props.data().value().length}
                    </div>
                }
            />
            <div className="mt1 " style={{ position: "relative", minHeight: "4rem" }}>
                {!isTyping() && filterListByText().length === 0 && (
                    <div className="flexRow flexVCenter flex-hcenter" style={{ height: "4rem" }}>
                        No matches
                    </div>
                )}
                <div
                    ref={refFilterComponentsContainer}
                    onDrop={(e) => {}}
                    onDragOver={(e) => e.preventDefault()}
                    style={{
                        transition: "filter .1s",
                        filter: isTyping() ? "contrast(.65) grayscale(1) " : "",
                    }}>
                    {filterListByText.map((it, _, i) => (
                        <div
                            onDragOver={(e) => e.preventDefault()}
                            class=" flexCol componentContainer">
                            <InputField data={it} validate={validateInputComplex} debounce={0}>
                                {(sig, handleOnBlur) => {
                                    return (
                                        <InputComplexComponent
                                            data={sig}
                                            options={props.options}
                                            handleOnBlur={() => {
                                                handleOnBlur();
                                            }}
                                            onCopy={props.onCopy}
                                            onDelete={props.onDelete}
                                        />
                                    );
                                }}
                            </InputField>
                            <div class="flexRow mt1">
                                <div class="flex-grow">
                                    <InlineError sig={it} ignoreTouched>
                                        {S(() => complexInputErrorMessage(it().error))}
                                    </InlineError>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div
                    class="pos-abs ptrEvNone flexRow flex-hcenter flexVCenter"
                    style={{
                        top: "0",
                        left: "0",
                        right: "0",
                        height: "100%",
                        maxHeight: "40rem",
                        margin: "auto auto",
                    }}>
                    {isTyping() && (
                        <div class="" style={{ height: "4rem" }}>
                            <Loader />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    function debounceInput(value: string, timeout = FILTER_INPUT_TIMEOUT) {
        if (timer === -1) setTimeout(updateFilter, timeout);
        lastInputTime = Date.now();
        filterUpdateValue = value;
    }

    function updateFilter() {
        timer = -1;
        const diff = Date.now() - lastInputTime;
        if (diff < FILTER_INPUT_TIMEOUT) {
            setTimeout(updateFilter, diff);
        } else {
            filter(filterUpdateValue);
        }
    }
}

export function InputComplexComponent(props: {
    data: DataSignal<InputComplexState>;
    handleOnBlur: () => void;
    options: DataSignal<{ value: string; id: string }[]>;
    onDelete: (index: DataSignal<InputComplexState>) => void;
    onCopy: (index: DataSignal<InputComplexState>) => void;
}): JSX.Element {
    const uuid = ++__uuids;
    const signalCheckbox = twoWayInputState(
        "checkbox" + uuid,
        props.data,
        (value) => value.a,
        (value) => ({ a: value }),
    );
    const signalSelect = twoWayInputState(
        "select" + uuid,
        props.data,
        (value) => value.b,
        (value) => ({ b: value }),
    );
    const signalText = twoWayInputState(
        "text" + uuid,
        props.data,
        (value) => value.c,
        (value) => ({ c: value }),
    );

    S(() => {
        const curr = S.sample(props.data);
        if (
            curr.a !== signalCheckbox().value ||
            curr.b !== signalSelect().value ||
            curr.c !== signalText().value
        ) {
            const next: InputComplexState = {
                a: signalCheckbox().value,
                b: signalSelect().value,
                c: signalText().value,
                type: curr.type,
            };
            props.data(next);
        }
    });

    return (
        <div
            draggable={false}
            onDragOver={(e) => e.preventDefault()}
            // onDrop={e => console.log("drop",e.target)}
            class="flexRow flexVEnd complexInput mb1"
            style={{ transition: "transform .1s" }}>
            {/*<div class="moveRow" aria-label="Move Row" role="button" style={{*/}
            {/*    height: "1.6em",*/}
            {/*    flex: "0 0 5rem",*/}
            {/*    cursor: "grab",*/}
            {/*    color: "#676767",*/}
            {/*}}*/}
            {/*>*/}
            {/*    <IconVertEllipsis/>*/}
            {/*</div>*/}
            <label
                class="label-inline ml0 mr1"
                style={{ flex: "0 0 auto", margin: "0", marginRight: ".8rem" }}
                for={signalCheckbox().name}>
                <InputCheckbox
                    data={signalCheckbox}
                    // onBlur={props.handleOnBlur}
                    // disabled={isDisabled}
                />
                <span>
                    <span class="disp-none">Check box</span>
                </span>
            </label>

            <Select
                style={{ flex: "0 0 160px", margin: "0", marginRight: ".8rem" }}
                classes={{ error: "fc error", default: "fc" }}
                data={signalSelect}
                // disabled={S(()=>props.inputState().isDisabled,S.sample(props.inputState).isDisabled)}
                // onBlur={props.handleOnBlur}
                options={props.options().map((it) => it.value)}
                required
            />

            <InputText
                style={{ margin: "0", marginRight: "1.2rem", flex: "0 1 250px" }}
                data={signalText}
                // disabled={S(()=>props.inputState().isDisabled,S.sample(props.inputState).isDisabled)}
                trimWhitespace
                // onBlur={props.handleOnBlur}
            />
            <Tooltip label={`My text is "${signalText().value}"`}>
                <div className="notification-icon dynamicTip ">
                    <IconInfo />
                </div>
            </Tooltip>
            <ButtonImg
                aria-label="Copy Row"
                class="font-txt"
                onClick={(ev) => setTimeout(() => props.onCopy(props.data), 100)}>
                <IconPlus />
            </ButtonImg>
            <span class="flexInlineRow flex-hcenter" style={{ flex: "0 0 7rem" }}>
                {props.data().type}
            </span>

            <ButtonImg
                aria-label="Delete Row"
                class="font-warn"
                onClick={(ev) => setTimeout(() => props.onDelete(props.data), 100)}>
                <IconMinus />
            </ButtonImg>
            <div className="flex-grow" />
        </div>
    );
}

function complexInputErrorMessage(error: Errors): string | null {
    return error === Errors.TextEqualType
        ? "Text cannot be the same as the type (colour)"
        : error === Errors.SelectAndEqualSix
        ? 'Cannot select checkbox if "6" selected in dropdown.'
        : null;
}

const enum Errors {
    TextEqualType = 5000,
    SelectAndEqualSix,
}

function validateInputComplex(value: InputComplexState) {
    return value.c.toLowerCase() === value.type.toLowerCase()
        ? Errors.TextEqualType
        : value.a && value.b === "6"
        ? Errors.SelectAndEqualSix
        : 0;
}

let seed = 423432;

function getPredictableRandom() {
    const bit = ((seed >>> 0) ^ (seed >>> 2) ^ (seed >>> 6) ^ (seed >>> 7)) & 1;
    const out = (seed >>> 1) | (bit << 30);
    seed = (out >>> 0) | 0;
    return out;
}
